import React from "react";
import {useLocation} from "react-router-dom";
import {HazardousState, HazardType, Material, RiskConstructionType} from "../types/Enums";
import {translate, translateOther} from "../types/EnumTranslations";
import {
    BaseComponent,
    HazardousModel, ValueStatus
} from "../types/protocols";
import {WarningData} from "../Components/WarningViewComponent";

/**
 * Extracts the building key from the URL.
 * Assumes the URL structure is /building/buildingKey/...
 * @returns {string | null} The building key, or null if not found.
 */
export const useBuildingKey = (): string | null => {
    const location = useLocation();
    const pathSegments = location.pathname.split("/").filter(Boolean); // Remove empty segments
    return pathSegments.length >= 2 ? pathSegments[1] : null; // Return the second segment
};

const COLORS = {
    primary: '#3cb6df',
    primaryDark: '#369ec3',
    deepGreen: '#36c356',
    deepRed: '#c33636',
    primaryDarkHover: '#ffffff',
    purple: '#9636c3',
    secondary: '#555',
    background: '#f4f4f4',
    redOrange: '#fd795f',
    gold: '#c39836',
    yellow: '#f5c45f',
    text: '#000',
    highlight: '#e0f7fa',
    lightLightGrey: '#f7f6f7',
    lightGrey: '#EFEFEF',
    middleGrey: '#b3b3b3',
};

export const displayHazards = (hazardous?: HazardousModel): JSX.Element | null => {
    if (!hazardous) return null;

    const hazardEntries = Object.entries(hazardous).filter(
        ([_, state]) =>
            state === HazardousState.Suspected || state === HazardousState.Confirmed
    );

    if (hazardEntries.length === 0) return null;

    return (
        <>
            {hazardEntries.map(([hazard, state]) => (
                <>
                    {`${translateOther(hazard)} ${translateOther(state).toLowerCase()}`}<br/>
                </>
            ))}
        </>
    );
};

export function analyzeComponents(components: BaseComponent[]): string[] {
    const analysis: string[] = [];

    analysis.push(components.length === 1 ? `${components.length} del` : `${components.length} delar`);

    let totalArea = components.reduce((sum, comp) => {
        if ('width' in comp && 'height' in comp &&
            typeof comp.width === 'number' &&
            typeof comp.height === 'number') {
            return sum + (comp.width * comp.height);
        }
        return sum;
    }, 0);

    totalArea = totalArea / 1000000;
    if (totalArea > 0) {
        analysis.push(`${totalArea.toFixed(2)} kvm`);
    }

    const allMaterials = new Set<Material>();
    components.forEach(comp => {
        if (comp.material && Array.isArray(comp.material)) {
            comp.material.forEach((mat: Material) => allMaterials.add(mat));
        }
    });

    if (allMaterials.size > 0) {
        const translatedMaterials = Array.from(allMaterials)
            .map(material => translate(material).toLowerCase())
            .join(', ');
        analysis.push(`Material: ${translatedMaterials}`);
    }

    const uniqueRisks = new Set<string>();
    components.forEach(comp => {
        if (comp.buildingRisks?.value?.risks) {
            comp.buildingRisks.value?.risks.forEach(risk => {
                uniqueRisks.add(translate(risk));
            });
        }
        if (comp.buildingRisks?.value?.customRisk) {
            uniqueRisks.add(comp.buildingRisks.value?.customRisk);
        }
    });

    if (uniqueRisks.size > 0) {
        analysis.push('Riskkonstruktion:');
        Array.from(uniqueRisks).forEach(risk => {
            analysis.push(`- ${risk}`);
        });
    }

    const uniqueHazards = new Set<string>();
    components.forEach(comp => {
        if (comp.hazardous) {
            Object.entries(comp.hazardous).forEach(([hazardType, state]) => {
                if (state) {
                    uniqueHazards.add(translateOther(hazardType));
                }
            });
        }
    });

    if (uniqueHazards.size > 0) {
        analysis.push('Hälso/miljöfara: ' + Array.from(uniqueHazards).join(', '));
    }

    return analysis;
}

/**
 * Function to get all unique risk types from a list of components, with translations
 * @param components Array or object of BaseComponent objects with risk information
 * @param translations Optional mapping of risk type to translated string
 * @returns Array of strings containing translated risk types and custom risks
 */
export function getAllUniqueRiskTypes(components: BaseComponent[] | { [key: string]: BaseComponent }): string[] {
    const uniqueRisks = new Set<string>();

    // Convert to array if object is provided
    const componentArray = Array.isArray(components)
        ? components
        : Object.values(components);

    // Iterate through all components
    componentArray.forEach(component => {
        // Check if component has buildingRisks and its value is not null
        if (component.buildingRisks?.status === ValueStatus.set && component.buildingRisks.value) {
            const riskConstruction = component.buildingRisks.value;

            // Add standard risk types if present
            if (riskConstruction.risks && riskConstruction.risks.length > 0) {
                riskConstruction.risks.forEach(risk => {
                    uniqueRisks.add(translate(risk));
                });
            }

            // Add custom risk if present
            if (riskConstruction.customRisk) {
                uniqueRisks.add(riskConstruction.customRisk);
            }
        }
    });

    // Convert Set to Array and return
    return Array.from(uniqueRisks);
}

export function getRiskWarnings(
    components: BaseComponent[] | { [key: string]: BaseComponent }
): WarningData[] {
    // Get all unique risk types
    const riskTypes = getAllUniqueRiskTypes(components);

    // Convert each risk type to a WarningData object
    return riskTypes.map(riskType => ({
        title: riskType,
        items: []
    }));
}

export function getAllUniqueHazards(
    components: BaseComponent[] | { [key: string]: BaseComponent }
): Map<string, HazardousState> {
    const hazardMap = new Map<string, HazardousState>();

    // Convert to array if object is provided
    const componentArray = Array.isArray(components)
        ? components
        : Object.values(components);

    // Iterate through all components
    componentArray.forEach(component => {
        // Check if component has hazardous and its value is not null
        if (component.hazardous?.status === ValueStatus.set && component.hazardous.value) {
            const hazardModel = component.hazardous.value;

            // Process each hazard type
            if (hazardModel.asbestos) {
                updateHazardMap(hazardMap, HazardType.HazardAsbestos, hazardModel.asbestos);
            }
            if (hazardModel.pcb) {
                updateHazardMap(hazardMap, HazardType.HazardPcb, hazardModel.pcb);
            }
            if (hazardModel.led) {
                updateHazardMap(hazardMap, HazardType.HazardLed, hazardModel.led);
            }
            if (hazardModel.arsenic) {
                updateHazardMap(hazardMap, HazardType.HazardArsenic, hazardModel.arsenic);
            }
            if (hazardModel.blueConcrete) {
                updateHazardMap(hazardMap, HazardType.HazardBlueConcrete, hazardModel.blueConcrete);
            }
            if (hazardModel.radon) {
                updateHazardMap(hazardMap, HazardType.HazardRadon, hazardModel.radon);
            }
        }
    });

    return hazardMap;
}

/**
 * Helper function to update the hazard map
 * @param map The map to update
 * @param hazardKey The key for the hazard
 * @param status The new status to consider
 */
function updateHazardMap(
    map: Map<string, HazardousState>,
    hazardKey: string,
    status: HazardousState
): void {
    // If not in map yet, add it
    if (!map.has(hazardKey)) {
        map.set(hazardKey, status);
        return;
    }

    // If already CONFIRMED, keep it
    if (map.get(hazardKey) === HazardousState.Confirmed) {
        return;
    }

    // If new status is CONFIRMED, update it (CONFIRMED takes precedence over SUSPECTED)
    if (status === HazardousState.Confirmed) {
        map.set(hazardKey, HazardousState.Confirmed);
    }
}

/**
 * Get all unique hazards from a list of components
 * @param components Array or object of BaseComponent objects with hazard information
 * @returns Array of WarningData objects for hazards
 */
export function getHazardWarnings(
    components: BaseComponent[] | { [key: string]: BaseComponent }
): WarningData[] {
    const hazardMap = getAllUniqueHazards(components);
    const warningData: WarningData[] = [];

    // Convert hazard map to warning data
    hazardMap.forEach((state, hazardKey) => {
        warningData.push({
            title: `${translate(hazardKey)} - ${translate(state)}`,
            items: []
        });
    });

    return warningData;
}


export default COLORS;